import PersistentStore from "@/api/PersistentStore";
import requests from "@/api/request";
import { closeWindow, postMessage } from "@/components/shared/util";
import router from "@/router";
import { RootState } from "@/store/types";
import { ActionTree, Commit } from "vuex";
import { DARK_MODE_KEY, STATIC_TITLE_KEY } from ".";
import { Patient } from "../patient/types";
import { NavigationState } from "./types";

const popupToNewWindow: Record<string, Record<string, any>> = {
  tower: {
    routerRoute: "tower",
    preCheck: (rootGetters: any) => {
      const patient = rootGetters["patient/current"] as Patient;
      let ageKnown;
      if (patient.census.age < 0) {
        ageKnown = confirm(
          "Patient DOB not entered.\n" +
            "\nClick OK if the patient 18 or older." +
            "\nClick Cancel if not."
        );
      } else {
        ageKnown = true;
      }
      return ageKnown;
    }
  },
  review: { routerRoute: "tower" },
  bypass: { routerRoute: "tower" },
  PostOpReview: { routerRoute: "tower" }
};

function removePopupFromRouter(getters: any) {
  const from = getters.route.from;
  // Checking if we opened the page to this route, back will not be available
  if (from.name === null) {
    router.push({ query: { popupName: undefined } });
  } else {
    router.back();
  }
}

function getNewWindowPopupConfig(popupName: string, getters: any) {
  if (!getters.inWrapper) {
    return popupToNewWindow[popupName];
  } else {
    return null;
  }
}

export const actions: ActionTree<NavigationState, RootState> = {
  showPopup({ commit, getters, rootGetters }, popupName) {
    if (typeof popupName !== "string") {
      throw "Unexpected popupName";
    }

    // Prevent opening multiple windows
    if (getters.openedWindows.includes(popupName)) {
      return;
    }

    const newWindowPopupConfig = getNewWindowPopupConfig(popupName, getters);
    if (newWindowPopupConfig) {
      let preCheckResult = true;
      if (newWindowPopupConfig.preCheck) {
        preCheckResult = newWindowPopupConfig.preCheck(rootGetters);
      }
      if (preCheckResult) {
        openNewWindow(commit, newWindowPopupConfig.routerRoute, popupName);
      }
      return;
    }

    if (popupName === router.currentRoute.query.popupName) {
      // Close if clicked again
      removePopupFromRouter(getters);
    } else if (!router.currentRoute.query.popupName) {
      // Push if no other popups showing
      router.push({
        query: {
          popupName
        }
      });
    } else {
      // Replace if popup currently showing
      router.replace({
        query: {
          popupName
        }
      });
    }
  },
  hidePopup({ getters }) {
    const currentPopupName = router.currentRoute.params.page;
    const newWindowPopupConfig = getNewWindowPopupConfig(
      currentPopupName,
      getters
    );
    if (newWindowPopupConfig) {
      closeWindow(currentPopupName);
    } else {
      removePopupFromRouter(getters);
    }
  },
  selectId({ commit }, id) {
    commit("currentId", id);
  },
  darkMode({ commit }, isDark) {
    const mode = isDark ? "dark" : "light";
    PersistentStore.setItem(DARK_MODE_KEY, { mode });
    commit("darkMode", mode);
  },
  darkModeOs({ commit }, isDark) {
    commit("darkModeOs", isDark);
  },
  inWrapper({ commit }) {
    commit("inWrapper");
  },
  goHome() {
    const message = {
      action: "navigate",
      target: "main"
    };
    postMessage(message);
  },
  log(_unused, text) {
    const message = {
      action: "log",
      text
    };

    postMessage(message);
  },
  externalLink(_unused, uri) {
    const message = {
      action: "link", // "link", "navigate"
      target: "external",
      uri
    };
    postMessage(message);
  },
  setHelpStep({ commit }, step) {
    commit("setHelpStep", step);
  },
  setMobilePage({ commit }, page) {
    commit("setMobilePage", page);
  },
  toggleLegendMobile({ commit }) {
    commit("toggleLegendMobile");
  },
  redirectTo({ commit }, to) {
    commit("redirectTo", to);
  },
  censusPreference({ commit, rootGetters }, pref) {
    const area = rootGetters["patient/area"];
    commit("censusPreference", { area, pref });
  },

  staticTitle({ commit }, staticTitle) {
    PersistentStore.setItem(STATIC_TITLE_KEY, staticTitle);
    commit("staticTitle", staticTitle);
  },

  async redirectRequest(_unused, id): Promise<string | boolean> {
    const data = await requests.redirectRequest(id);
    const good = data.status && data.status === "good";
    if (good) {
      return data.url;
    } else {
      return false;
    }
  },

  async logLinkClickToServer(_unused, href) {
    const parms = { recordType: "Link" };
    const data = { data: href };
    await requests.logRequest(parms, data);
  }
};
function openNewWindow(commit: Commit, routerRoute: string, popupName: string) {
  if (!window.awCommunicationChannel.close) {
    window.awCommunicationChannel.close = function(
      name: string,
      needsToBeClosed: boolean
    ) {
      commit("closeWindow", name);
      if (needsToBeClosed) {
        // Close it if coming from close,
        // will be called again and go into unload block below
        window.awCommunicationChannel.openedWindows[name].close();
      } else {
        // Remove it if comming from unload
        delete window.awCommunicationChannel.openedWindows[name];
      }
    };
  }

  const { area, room } = router.currentRoute.params;
  const openedWindow = window.open(
    `#/${area}/${routerRoute}/${room}/${popupName}`,
    "_blank",
    `toolbar=no,location=no,directories=no,
        width=960,height=720,status=no,menubar=no,
        scrollbars=yes,resizable=yes,left=20,top=20`
  );
  window.awCommunicationChannel.openedWindows[popupName] = openedWindow;
  commit("openWindow", popupName);
}
